import dayjs from 'dayjs'
import type { CreateAvailabilityInput } from '@jupi/shared'
import { clubInstant, clubToday } from './clubTime'

// The "add availability" form as plain data, and what it turns into: kept out
// of the component so the rules can be tested.

export interface AvailabilityDraft {
  from: string
  // Empty: repeat until removed.
  until: string
  weekdays: number[]
  startTime: string
  endTime: string
}

// The weekday names, 1 = Monday: a Monday in January 2024 and on.
export const weekdayName = (weekday: number, language: string, style: 'long' | 'short' = 'long') =>
  new Intl.DateTimeFormat(language, { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, weekday)))

export const DAY_PRESETS = [
  { key: 'weekdays', days: [1, 2, 3, 4, 5] },
  { key: 'weekends', days: [6, 7] },
  { key: 'everyDay', days: [1, 2, 3, 4, 5, 6, 7] },
] as const

export const TIME_PRESETS = [
  { key: 'morning', from: '08:00', to: '12:00' },
  { key: 'daytime', from: '08:00', to: '17:00' },
  { key: 'afternoon', from: '12:00', to: '17:00' },
  { key: 'evening', from: '17:00', to: '21:00' },
  { key: 'allDay', from: '08:00', to: '21:00' },
] as const

export const blank = (): AvailabilityDraft => ({
  from: clubToday(),
  until: '',
  weekdays: [],
  startTime: '17:00',
  endTime: '20:00',
})

// ISO weekday (1 = Monday) of a YYYY-MM-DD date.
export const isoWeekday = (date: string) => dayjs(date).day() || 7

// The draft for days picked in the month view: the days themselves, or the
// one day with its own weekday. The evening is the usual offer.
export function draftForDays(start: Date, end: Date): AvailabilityDraft {
  const from = dayjs(start).format('YYYY-MM-DD')
  const until = dayjs(end).subtract(1, 'millisecond').format('YYYY-MM-DD')
  const days = new Set<number>()
  for (let d = dayjs(from); !d.isAfter(until, 'day') && days.size < 7; d = d.add(1, 'day')) days.add(d.day() || 7)
  return {
    from,
    until: until < from ? from : until,
    weekdays: [1, 2, 3, 4, 5, 6, 7].filter((d) => days.has(d)),
    startTime: '17:00',
    endTime: '20:00',
  }
}

// One day only: Until is the same day as From, so the weekdays do not apply.
export const isSingleDay = (draft: AvailabilityDraft) => draft.until !== '' && draft.until === draft.from

// Why the draft cannot be saved, as an i18n key under `lessons`, or null.
export function draftFault(draft: AvailabilityDraft): 'endAfterStart' | 'untilBeforeFrom' | 'pickDays' | null {
  if (draft.endTime <= draft.startTime) return 'endAfterStart'
  if (draft.until && draft.until < draft.from) return 'untilBeforeFrom'
  if (!isSingleDay(draft) && draft.weekdays.length === 0) return 'pickDays'
  return null
}

// What saving sends: one window for a single day, otherwise one weekly rule
// per chosen weekday, valid from From to Until (or until removed).
export function requestsFor(draft: AvailabilityDraft): CreateAvailabilityInput[] {
  if (isSingleDay(draft)) {
    return [
      {
        kind: 'window',
        startsAt: clubInstant(draft.from, draft.startTime),
        endsAt: clubInstant(draft.from, draft.endTime),
      },
    ]
  }
  return draft.weekdays.map((weekday) => ({
    kind: 'weekly' as const,
    weekday,
    startTime: draft.startTime,
    endTime: draft.endTime,
    validFrom: draft.from,
    validTo: draft.until || null,
  }))
}
